import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { domainSuppressionsApi } from '@/api/domainSuppressions'
import type { DomainSuppressionEntryDto, DomainSuppressionImportResultDto } from '@/types/domainSuppressions'

/** Result of a mutation. `handled` is true when a server 422 was already toasted by the global handler. */
export interface MutationResult {
  ok: boolean
  error?: string
  handled?: boolean
}

export interface ImportMutationResult extends MutationResult {
  result?: DomainSuppressionImportResultDto
}

export const DOMAIN_SUPPRESSION_PAGE_SIZE = 25

/**
 * Loads + maintains one page of a company's ACTIVE blocked domains, with a domain search, plus the admin
 * mutations (manual add, remove, bulk import). Each mutation reloads the current page on success. A
 * generation token guards against stale loads when the company or search changes. Mirrors useSuppressions.
 */
export function useDomainSuppressions(clientCompanyId: string) {
  const [items, setItems] = useState<DomainSuppressionEntryDto[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0) // zero-based
  const requestIdRef = useRef(0)

  const load = useCallback(async () => {
    if (!clientCompanyId) return
    const reqId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    const res = await domainSuppressionsApi.list(clientCompanyId, {
      search: search.trim() || undefined,
      active: true,
      skip: page * DOMAIN_SUPPRESSION_PAGE_SIZE,
      take: DOMAIN_SUPPRESSION_PAGE_SIZE,
    })
    if (requestIdRef.current !== reqId) return
    if (res.error) setError(res.error)
    if (res.data) {
      setItems(res.data.Items)
      setTotal(res.data.Total)
    }
    setLoading(false)
  }, [clientCompanyId, search, page])

  useEffect(() => {
    void load()
    return () => { requestIdRef.current++ }
  }, [load])

  const addManual = useCallback(async (domain: string, reason?: string): Promise<MutationResult> => {
    if (!clientCompanyId) return { ok: false, error: 'No company selected.' }
    const res = await domainSuppressionsApi.add(clientCompanyId, { Domain: domain.trim(), Reason: reason?.trim() || undefined })
    if (res.error || !res.data) {
      if (res.status === 422) return { ok: false, handled: true }
      return { ok: false, error: res.error || 'Could not add domain.' }
    }
    await load()
    return { ok: true }
  }, [clientCompanyId, load])

  const remove = useCallback(async (domain: string): Promise<MutationResult> => {
    if (!clientCompanyId) return { ok: false, error: 'No company selected.' }
    const res = await domainSuppressionsApi.remove(clientCompanyId, domain)
    // DELETE returns 204 (no body, no error). Any other error surfaces.
    if (res.error) {
      if (res.status === 422) return { ok: false, handled: true }
      return { ok: false, error: res.error }
    }
    await load()
    return { ok: true }
  }, [clientCompanyId, load])

  const importList = useCallback(async (domains: string[]): Promise<ImportMutationResult> => {
    if (!clientCompanyId) return { ok: false, error: 'No company selected.' }
    const res = await domainSuppressionsApi.import(clientCompanyId, { Domains: domains })
    if (res.error || !res.data) {
      if (res.status === 422) return { ok: false, handled: true }
      return { ok: false, error: res.error || 'Import failed.' }
    }
    await load()
    return { ok: true, result: res.data }
  }, [clientCompanyId, load])

  return {
    items, total, loading, error,
    search, setSearch, page, setPage, pageSize: DOMAIN_SUPPRESSION_PAGE_SIZE,
    reload: load, addManual, remove, importList,
  }
}
